/**
 * Menu de ações — o "⋯" que guarda o que é raro e o que é destrutivo.
 *
 * Existe porque cabeçalho de card tem espaço finito e nem toda ação merece
 * um botão permanente. Remover uma planta acontece uma vez por ano; deixá-la
 * do lado de "Posicionar", que acontece toda semana, é um clique errado
 * esperando para acontecer.
 *
 * O caminho oposto é pior e foi o que aconteceu aqui: esconder "remover
 * imagem" atrás do modo de posicionar deixou a ação INALCANÇÁVEL na prática
 * — quem queria trocar a planta não tinha por onde. Ação rara precisa de um
 * lugar previsível, não de um esconderijo.
 *
 * O balão vai pro <body> por portal e é posicionado em coordenada de
 * viewport pelo mesmo motivo do `Tip`: dentro de um card com `overflow`, um
 * menu absoluto chega cortado — e `z-index` não resolve recorte.
 */

import {
  useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import clsx from "clsx";

const MARGEM = 6;
const LARGURA = 224;

export interface AcaoDoMenu {
  rotulo: string;
  Icone?: typeof MoreVertical;
  onClick: () => void;
  /** Pinta de vermelho. Use para o que apaga. */
  destrutiva?: boolean;
  desabilitada?: boolean;
}

export function MenuDeAcoes({
  acoes, rotulo = "Mais ações",
}: {
  acoes: (AcaoDoMenu | false | null | undefined)[];
  rotulo?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const balaoRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const itens = acoes.filter(Boolean) as AcaoDoMenu[];

  useLayoutEffect(() => {
    if (!aberto) { setPos(null); return; }
    function calcular() {
      const g = gatilhoRef.current?.getBoundingClientRect();
      if (!g) return;
      const altura = balaoRef.current?.getBoundingClientRect().height ?? 0;
      // Abre pra baixo; sobe quando não cabe. Cortar não é opção.
      const cabeAbaixo = g.bottom + altura + MARGEM <= window.innerHeight;
      setPos({
        top: cabeAbaixo ? g.bottom + MARGEM : Math.max(MARGEM, g.top - altura - MARGEM),
        left: Math.min(
          Math.max(MARGEM, g.right - LARGURA),
          Math.max(MARGEM, window.innerWidth - LARGURA - MARGEM),
        ),
      });
    }
    calcular();
    window.addEventListener("scroll", calcular, true);
    window.addEventListener("resize", calcular);
    return () => {
      window.removeEventListener("scroll", calcular, true);
      window.removeEventListener("resize", calcular);
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    function fora(e: PointerEvent) {
      const alvo = e.target as Node;
      if (gatilhoRef.current?.contains(alvo)) return;
      if (balaoRef.current?.contains(alvo)) return;
      setAberto(false);
    }
    function tecla(e: KeyboardEvent) {
      if (e.key === "Escape") setAberto(false);
    }
    document.addEventListener("pointerdown", fora);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("pointerdown", fora);
      document.removeEventListener("keydown", tecla);
    };
  }, [aberto]);

  if (!itens.length) return null;

  return (
    <>
      <button
        ref={gatilhoRef}
        type="button"
        aria-label={rotulo}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        onClick={() => setAberto((v) => !v)}
        className="rounded-md border border-surface-300 p-1.5 text-slate-500 hover:bg-surface-50 hover:text-slate-700 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
      >
        <MoreVertical className="h-4 w-4" aria-hidden />
      </button>

      {aberto && createPortal(
        <div
          ref={balaoRef}
          id={id}
          role="menu"
          style={{
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            width: LARGURA,
            visibility: pos ? "visible" : "hidden",
          }}
          className="fixed z-[60] overflow-hidden rounded-lg border border-surface-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
        >
          {itens.map((a) => (
            <button
              key={a.rotulo}
              type="button"
              role="menuitem"
              disabled={a.desabilitada}
              onClick={() => { setAberto(false); a.onClick(); }}
              className={clsx(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:opacity-40",
                a.destrutiva
                  ? "text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                  : "text-slate-700 hover:bg-surface-100 dark:text-slate-200 dark:hover:bg-slate-700",
              )}
            >
              {a.Icone && <a.Icone className="h-4 w-4 shrink-0" aria-hidden />}
              {a.rotulo}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
